import React, { useMemo, useState } from 'react';
import {
	Button,
	Card,
	Input,
	Select,
	Space,
	Table,
} from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';

import Header from '../../../components/Header';
import QuestionDetailsModal from '../../Orders/ProductionQuestions/QuestionDetailsModal';
import QuestionFormModal from '../../Orders/ProductionQuestions/QuestionFormModal';
import QuestionStatusControl from '../../Orders/ProductionQuestions/QuestionStatusControl';
import { STATUS_OPTIONS } from '../../Orders/ProductionQuestions/constants';
import {
	ID_COLUMN,
	ORDER_LINE_ITEMS_COLUMN,
	REPORTER_COLUMN,
	RESOLUTION_CATEGORY_COLUMN,
	RESOLUTION_NOTES_COLUMN,
	RESOLVED_COLUMN,
} from '../../Orders/ProductionQuestions/tableColumns';
import type {
	ProductionQuestion,
	ProductionQuestionCategory,
	ProductionQuestionStatus,
} from '../../../interfaces/ProductionQuestion.interface';
import { useGetProductionQuestionsQuery } from '../../../redux/api/production-questions';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import { canEditProductionQuestion } from '../../../utils/canEditProductionQuestion';

const PAGE_SIZE = 25;

const STATUS_FILTER_OPTIONS: {
	label: string;
	value: ProductionQuestionStatus | '';
}[] = [{ label: 'All statuses', value: '' }, ...STATUS_OPTIONS];

const CATEGORY_OPTIONS: {
	label: string;
	value: ProductionQuestionCategory | '';
}[] = [
	{ label: 'All categories', value: '' },
	{ label: 'Rounding Error', value: 'rounding_error' },
	{ label: 'Model and Drawing Differ', value: 'model_drawing_differ' },
	{ label: 'Drawing Missing Information', value: 'drawing_missing_info' },
	{ label: 'Other', value: 'other' },
];

const ProductionQuestionsListPage: React.FC = () => {
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [status, setStatus] = useState<ProductionQuestionStatus | ''>('');
	const [category, setCategory] = useState<ProductionQuestionCategory | ''>('');
	const [statusSort, setStatusSort] = useState<SortOrder>(null);
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [editing, setEditing] = useState<ProductionQuestion | null>(null);
	const [viewing, setViewing] = useState<ProductionQuestion | null>(null);

	const openDetails = (row: ProductionQuestion) => {
		setViewing(row);
		setDetailsOpen(true);
	};

	const openEdit = (row: ProductionQuestion) => {
		setEditing(row);
		setFormModalOpen(true);
	};

	const { data: loggedInUser } = useGetLoggedInUserQuery();
	const { data, isFetching } = useGetProductionQuestionsQuery({
		page,
		page_size: PAGE_SIZE,
		search: search || undefined,
		status: status || undefined,
		category: category || undefined,
		ordering:
			statusSort === 'ascend'
				? 'status_rank,-created_time'
				: statusSort === 'descend'
					? '-status_rank,-created_time'
					: '-created_time',
	});

	const columns: ColumnsType<ProductionQuestion> = useMemo(
		() => [
			ID_COLUMN,
			ORDER_LINE_ITEMS_COLUMN,
			{
				title: 'Category',
				dataIndex: 'category_display',
				key: 'category_display',
			},
			REPORTER_COLUMN,
			RESOLUTION_CATEGORY_COLUMN,
			RESOLUTION_NOTES_COLUMN,
			RESOLVED_COLUMN,
			{
				title: 'Status',
				key: 'status',
				sorter: true,
				sortOrder: statusSort,
				render: (_, row) => (
					<QuestionStatusControl
						question={row}
						onUpdated={(updated) => {
							if (viewing?.id === updated.id) {
								setViewing(updated);
							}
						}}
					/>
				),
			},
			{
				title: 'Actions',
				key: 'actions',
				align: 'right',
				width: 180,
				render: (_, row) => (
					<Space size="small" className="whitespace-nowrap">
						{canEditProductionQuestion(loggedInUser, row) && (
							<Button
								type="link"
								icon={<EditOutlined />}
								onClick={() => openEdit(row)}
							>
								Edit
							</Button>
						)}
						<Button onClick={() => openDetails(row)}>Details</Button>
					</Space>
				),
			},
		],
		[viewing?.id, loggedInUser, statusSort]
	);

	const handleTableChange: TableProps<ProductionQuestion>['onChange'] = (
		_pagination,
		_filters,
		sorter
	) => {
		const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
		const nextSort =
			activeSorter?.columnKey === 'status' ? (activeSorter.order ?? null) : null;
		if (nextSort !== statusSort) {
			setStatusSort(nextSort);
			setPage(1);
		}
	};

	const pagination: TablePaginationConfig = {
		current: page,
		pageSize: PAGE_SIZE,
		total: data?.count ?? 0,
		onChange: (nextPage) => setPage(nextPage),
		showSizeChanger: false,
	};

	const applySearch = () => {
		setSearch(searchInput.trim());
		setPage(1);
	};

	return (
		<>
			<Header
				title="Production Questions"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Production' },
					{ title: 'Production Questions' },
				]}
				showOnClickButton
				buttonText="New Question"
				icon={<PlusOutlined />}
				handleOnClick={() => {
					setEditing(null);
					setFormModalOpen(true);
				}}
			/>

			<Card className="shadow-sm">
				<Space wrap size="middle" className="mb-4">
					<Input
						allowClear
						prefix={<SearchOutlined />}
						placeholder="Search ID, order, or line item"
						style={{ width: 280 }}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onPressEnter={applySearch}
						onBlur={applySearch}
					/>
					<Select
						value={status}
						options={STATUS_FILTER_OPTIONS}
						style={{ width: 200 }}
						onChange={(value) => {
							setStatus(value);
							setPage(1);
						}}
					/>
					<Select
						value={category}
						options={CATEGORY_OPTIONS}
						style={{ width: 260 }}
						onChange={(value) => {
							setCategory(value);
							setPage(1);
						}}
					/>
				</Space>

				<Table
					rowKey="id"
					columns={columns}
					dataSource={data?.results ?? []}
					loading={isFetching}
					pagination={pagination}
					onChange={handleTableChange}
					size="middle"
				/>
			</Card>

			<QuestionFormModal
				open={formModalOpen}
				onClose={() => {
					setFormModalOpen(false);
					setEditing(null);
				}}
				question={editing}
			/>

			<QuestionDetailsModal
				question={viewing}
				open={detailsOpen}
				onClose={() => setDetailsOpen(false)}
				afterClose={() => setViewing(null)}
				showLineItemLinks
				onQuestionUpdated={setViewing}
				onEdit={
					viewing && canEditProductionQuestion(loggedInUser, viewing)
						? () => {
								setDetailsOpen(false);
								openEdit(viewing);
							}
						: undefined
				}
			/>
		</>
	);
};

export default ProductionQuestionsListPage;
